'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
  Grid,
  IconButton,
  Chip,
  Dialog,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  alpha,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Star,
  StarBorder,
  LocationOn,
  ArrowBack,
  Close,
  LocalShipping,
  Person,
  Phone,
  Badge,
  Place,
  Apartment,
  Map,
  Numbers,
  Signpost,
} from '@mui/icons-material';
import { formatApiError } from '../../services/api/apiHelpers';
import { motion, AnimatePresence } from 'framer-motion';
import { BUSINESS_COLORS } from '@/app/business-dashboard/utils/constants';
import { useTranslation } from '@/kit/useTranslation';
import { LayoutContainer } from '@/kit/LayoutContainer';
import AddressAutocomplete from '@/components/AddressAutocomplete/AddressAutocomplete';
import useApiCall from '@/services/api/useApiCall';
import { buildApiConfig } from '@/services/api/apiClient';
import { endPointEnums } from '@/services/api/endpoints';
import { lookupPincode } from '@/utils/pincodeLookup';

// Motion components
const MotionBox = motion.create(Box);
const MotionCard = motion.create(Card);
const MotionPaper = motion.create(Paper);

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 300, damping: 24 },
  },
  exit: { opacity: 0, x: -100, transition: { duration: 0.2 } },
};

const formVariants = {
  hidden: { opacity: 0, x: 50 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { type: 'spring', stiffness: 300, damping: 30 },
  },
};

// Address type configurations
const ADDRESS_TYPES = {
  PICKUP: {
    labelKey: 'savedAddresses.pickup',
    color: '#10b981',
    bgColor: alpha('#10b981', 0.08),
    icon: LocalShipping,
  },
  DELIVERY: {
    labelKey: 'savedAddresses.delivery',
    color: '#3b82f6',
    bgColor: alpha('#3b82f6', 0.08),
    icon: Place,
  },
  BOTH: {
    labelKey: 'savedAddresses.both',
    color: '#8b5cf6',
    bgColor: alpha('#8b5cf6', 0.08),
    icon: Map,
  },
};

// Modern Address Card - Compact version
function AddressCard({ address, onEdit, onDelete, onSetDefault, isSelected }) {
  const { t } = useTranslation();

  // Normalize data for UI (handle savedAddressPrefix if arriving from specific fetchers)
  const normalizedAddress = {
    id: address.savedAddressId || address.addressId || address.id,
    label: address.savedAddressLabel || address.addressLabel || '',
    type: address.savedAddressType || address.addressType || 'PICKUP',
    fullAddress: address.savedAddressFullAddress || address.fullAddress || '',
    city: address.savedAddressCity || address.city || '',
    state: address.savedAddressState || address.state || '',
    pincode: address.savedAddressPincode || address.pincode || '',
    isDefault: address.savedAddressIsDefault ?? address.isDefault ?? false,
    contactName: address.savedAddressContactPersonName || address.contactPersonName || '',
    contactPhone: address.savedAddressContactPersonPhone || address.contactPersonPhone || '',
  };

  const typeConfig = ADDRESS_TYPES[normalizedAddress.type] || ADDRESS_TYPES.PICKUP;
  const TypeIcon = typeConfig.icon;

  return (
    <MotionCard
      variants={itemVariants}
      layout
      whileHover={{ scale: 1.01, y: -2 }}
      whileTap={{ scale: 0.99 }}
      onClick={() => onEdit(address)}
      sx={{
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: isSelected
          ? '#6366f1'
          : normalizedAddress.isDefault
            ? alpha('#6366f1', 0.3)
            : '#e2e8f0',
        boxShadow: isSelected
          ? '0 8px 24px rgba(99, 102, 241, 0.15)'
          : normalizedAddress.isDefault
            ? '0 4px 12px rgba(99, 102, 241, 0.08)'
            : '0 2px 8px rgba(0, 0, 0, 0.04)',
        cursor: 'pointer',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'visible',
        position: 'relative',
        bgcolor: isSelected ? alpha('#6366f1', 0.02) : '#fff',
        mb: 0.5,
      }}
    >
      {normalizedAddress.isDefault && (
        <Box
          sx={{
            position: 'absolute',
            top: -10,
            right: 16,
            px: 1,
            py: 0.25,
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.4)',
            zIndex: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: '0.65rem',
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
            }}
          >
            {t('savedAddresses.starDefault')}
          </Typography>
        </Box>
      )}

      <CardContent sx={{ p: 2.5 }}>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2.5,
                  background: `linear-gradient(135deg, ${typeConfig.color} 0%, ${alpha(typeConfig.color, 0.7)} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 4px 12px ${alpha(typeConfig.color, 0.2)}`,
                }}
              >
                <TypeIcon sx={{ fontSize: 20, color: '#fff' }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontWeight: 800,
                    color: '#1e293b',
                    fontSize: '0.95rem',
                    lineHeight: 1.2,
                    mb: 0.5,
                  }}
                >
                  {normalizedAddress.label || t('savedAddresses.unnamedAddress')}
                </Typography>
                <Chip
                  label={t(typeConfig.labelKey)}
                  size="small"
                  sx={{
                    bgcolor: typeConfig.bgColor,
                    color: typeConfig.color,
                    fontWeight: 700,
                    fontSize: '0.65rem',
                    height: 20,
                    '& .MuiChip-label': { px: 1 },
                  }}
                />
              </Box>
            </Stack>

            <Stack direction="row" spacing={0.5}>
              <Tooltip
                title={
                  normalizedAddress.isDefault
                    ? t('savedAddresses.default')
                    : t('savedAddresses.setAsDefault')
                }
              >
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSetDefault(normalizedAddress.id);
                  }}
                  sx={{
                    color: normalizedAddress.isDefault ? '#f59e0b' : '#94a3b8',
                    p: 0.75,
                    bgcolor: normalizedAddress.isDefault ? alpha('#f59e0b', 0.05) : 'transparent',
                    '&:hover': {
                      color: '#f59e0b',
                      bgcolor: alpha('#f59e0b', 0.1),
                    },
                  }}
                >
                  {normalizedAddress.isDefault ? (
                    <Star sx={{ fontSize: 20 }} />
                  ) : (
                    <StarBorder sx={{ fontSize: 20 }} />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip title={t('common.delete')}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(address);
                  }}
                  sx={{
                    color: '#94a3b8',
                    p: 0.75,
                    '&:hover': {
                      color: '#ef4444',
                      bgcolor: alpha('#ef4444', 0.1),
                    },
                  }}
                >
                  <Delete sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          <Box>
            <Typography
              sx={{
                fontSize: '0.85rem',
                color: '#475569',
                lineHeight: 1.5,
                fontWeight: 500,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                mb: 1,
              }}
            >
              {normalizedAddress.fullAddress}
            </Typography>

            <Typography
              sx={{
                fontSize: '0.75rem',
                color: '#64748b',
                fontWeight: 500,
                bgcolor: '#f8fafc',
                py: 0.5,
                px: 1,
                borderRadius: 1.5,
                display: 'inline-block',
                border: '1px solid #f1f5f9',
              }}
            >
              {normalizedAddress.city}, {normalizedAddress.state} - {normalizedAddress.pincode}
            </Typography>

            {normalizedAddress.contactName && (
              <Stack
                direction="row"
                spacing={2}
                sx={{
                  mt: 2,
                  pt: 1.5,
                  borderTop: '1px dashed #e2e8f0',
                  color: '#64748b',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    fontWeight: 500,
                  }}
                >
                  <Person sx={{ fontSize: 16, color: '#94a3b8' }} />
                  {normalizedAddress.contactName}
                </Typography>
                {normalizedAddress.contactPhone && (
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      fontWeight: 500,
                    }}
                  >
                    <Phone sx={{ fontSize: 16, color: '#94a3b8' }} />
                    {normalizedAddress.contactPhone}
                  </Typography>
                )}
              </Stack>
            )}
          </Box>
        </Stack>
      </CardContent>
    </MotionCard>
  );
}

// Address Form Component
function AddressForm({
  formData,
  onChange,
  onSave,
  onClear,
  loading,
  setFormData,
  isEditing,
  isDesktop,
}) {
  const { t } = useTranslation();
  return (
    <MotionPaper
      variants={formVariants}
      initial="hidden"
      animate="visible"
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
        height: 'fit-content',
        position: isDesktop ? 'sticky' : 'relative',
        top: isDesktop ? 24 : 0,
      }}
    >
      <Box
        sx={{
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          py: 2.5,
          px: 3,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              bgcolor: alpha('#fff', 0.2),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {isEditing ? <Edit sx={{ color: '#fff' }} /> : <Add sx={{ color: '#fff' }} />}
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: '1.1rem' }}>
              {isEditing ? t('savedAddresses.editAddress') : t('savedAddresses.addNewAddress')}
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: alpha('#fff', 0.8) }}>
              {isEditing ? t('savedAddresses.updateDetails') : t('savedAddresses.fillDetails')}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Box sx={{ p: 3 }}>
        <Stack spacing={2.5}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('savedAddresses.addressLabel')}
                placeholder={t('savedAddresses.addressLabelPlaceholder')}
                value={formData.addressLabel}
                onChange={onChange('addressLabel')}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Badge sx={{ color: '#94a3b8', fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('savedAddresses.type')}</InputLabel>
                <Select
                  value={formData.addressType}
                  label={t('savedAddresses.type')}
                  onChange={onChange('addressType')}
                  sx={{ borderRadius: 2 }}
                >
                  {Object.entries(ADDRESS_TYPES).map(([key, { labelKey, icon: Icon, color }]) => (
                    <MenuItem key={key} value={key}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Icon sx={{ fontSize: 18, color }} />
                        <span>{t(labelKey)}</span>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box>
            <AddressAutocomplete
              label={t('savedAddresses.searchAddress')}
              value={formData.fullAddress}
              onPlaceSelected={(place) => {
                setFormData((prev) => ({
                  ...prev,
                  fullAddress: place.address || place.fullAddress || '',
                  latitude: place.lat || place.latitude,
                  longitude: place.lng || place.longitude,
                  city: place.city || '',
                  state: place.state || '',
                  pincode: place.pincode || place.postalCode || '',
                }));
              }}
              onChange={(value) => {
                setFormData((prev) => ({ ...prev, fullAddress: value }));
              }}
              placeholder={t('savedAddresses.searchPlaceholder')}
            />
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label={t('savedAddresses.city')}
                value={formData.city}
                onChange={onChange('city')}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Apartment sx={{ color: '#94a3b8', fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label={t('savedAddresses.state')}
                value={formData.state}
                onChange={onChange('state')}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Map sx={{ color: '#94a3b8', fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label={t('savedAddresses.pincode')}
                value={formData.pincode}
                onChange={onChange('pincode')}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Numbers sx={{ color: '#94a3b8', fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
          </Grid>

          <TextField
            fullWidth
            label={t('savedAddresses.landmark')}
            placeholder={t('savedAddresses.landmarkPlaceholder')}
            value={formData.landmark}
            onChange={onChange('landmark')}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Signpost sx={{ color: '#94a3b8', fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />

          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: '#f8fafc',
              border: '1px solid #e2e8f0',
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '0.85rem',
                color: '#475569',
                mb: 1.5,
              }}
            >
              {t('savedAddresses.contactPerson')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('savedAddresses.name')}
                  value={formData.contactPersonName}
                  onChange={onChange('contactPersonName')}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person sx={{ color: '#94a3b8', fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fff',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('savedAddresses.phone')}
                  value={formData.contactPersonPhone}
                  onChange={onChange('contactPersonPhone')}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Phone sx={{ color: '#94a3b8', fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fff',
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </Box>

      <Box
        sx={{
          px: 3,
          py: 2,
          borderTop: '1px solid #e2e8f0',
          bgcolor: '#fafafa',
        }}
      >
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button
            variant="outlined"
            onClick={onClear}
            disabled={loading}
            sx={{
              px: 3,
              borderRadius: 2,
              fontWeight: 600,
              textTransform: 'none',
              borderColor: '#e2e8f0',
              color: '#64748b',
              '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' },
            }}
          >
            {t('savedAddresses.clear')}
          </Button>
          <Button
            variant="contained"
            onClick={onSave}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{
              px: 4,
              borderRadius: 2,
              fontWeight: 600,
              textTransform: 'none',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              boxShadow: '0 4px 14px rgba(99, 102, 241, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                boxShadow: '0 6px 20px rgba(99, 102, 241, 0.4)',
              },
            }}
          >
            {isEditing ? t('savedAddresses.updateAddress') : t('savedAddresses.saveAddress')}
          </Button>
        </Stack>
      </Box>
    </MotionPaper>
  );
}

// Delete Confirmation Dialog
function DeleteDialog({ open, onClose, onConfirm, address, loading }) {
  const { t } = useTranslation();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      <Box sx={{ p: 3 }}>
        <Stack spacing={3} alignItems="center" textAlign="center">
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              bgcolor: alpha('#ef4444', 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Delete sx={{ fontSize: 32, color: '#ef4444' }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              {t('savedAddresses.deleteAddress')}
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
              {t('savedAddresses.deleteConfirmation', { label: address?.addressLabel })}
            </Typography>
          </Box>
          <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={onClose}
              sx={{
                borderRadius: 2,
                fontWeight: 600,
                textTransform: 'none',
                borderColor: '#e2e8f0',
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={onConfirm}
              disabled={loading}
              sx={{
                borderRadius: 2,
                fontWeight: 600,
                textTransform: 'none',
                bgcolor: '#ef4444',
                '&:hover': { bgcolor: '#dc2626' },
              }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : t('common.delete')}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Dialog>
  );
}

// Mobile Form Dialog
function MobileFormDialog({ open, onClose, children }) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onClose={onClose} fullScreen PaperProps={{ sx: { bgcolor: '#f8fafc' } }}>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#fff' }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
            <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
              {t('savedAddresses.addressDetails')}
            </Typography>
          </Stack>
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto' }}>{children}</Box>
      </Box>
    </Dialog>
  );
}

// Main Component
export default function SavedAddressesPage() {
  const router = useRouter();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const { makeApiCall } = useApiCall();
  const { t } = useTranslation();

  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editingAddress, setEditingAddress] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState(null);
  const [mobileFormOpen, setMobileFormOpen] = useState(false);

  const initialFormData = {
    addressLabel: '',
    addressType: 'PICKUP',
    fullAddress: '',
    city: '',
    state: '',
    pincode: '',
    latitude: null,
    longitude: null,
    isDefault: false,
    landmark: '',
    contactPersonName: '',
    contactPersonPhone: '',
  };

  const [formData, setFormData] = useState(initialFormData);

  const fetchAddresses = async () => {
    setLoading(true);
    try {
      const config = buildApiConfig(endPointEnums.GET_SAVED_ADDRESSES);
      const response = await makeApiCall(config);
      if (response?.success) {
        setAddresses(response.data || []);
      }
    } catch (err) {
      console.error('[SavedAddresses] Fetch error:', err);
      setError(t('savedAddresses.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  // Fetch addresses on mount
  useEffect(() => {
    fetchAddresses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectAddress = (address) => {
    setEditingAddress(address);
    // Normalize both prefixed (savedAddressXxx) and flat field names from API
    setFormData({
      addressLabel: address.savedAddressLabel || address.addressLabel || '',
      addressType: address.savedAddressType || address.addressType || 'PICKUP',
      fullAddress: address.savedAddressFullAddress || address.fullAddress || '',
      city: address.savedAddressCity || address.city || '',
      state: address.savedAddressState || address.state || '',
      pincode: address.savedAddressPincode || address.pincode || '',
      latitude: address.savedAddressLatitude || address.latitude || null,
      longitude: address.savedAddressLongitude || address.longitude || null,
      isDefault: address.savedAddressIsDefault ?? address.isDefault ?? false,
      landmark: address.savedAddressLandmark || address.landmark || '',
      contactPersonName: address.savedAddressContactPersonName || address.contactPersonName || '',
      contactPersonPhone:
        address.savedAddressContactPersonPhone || address.contactPersonPhone || '',
    });
    if (!isDesktop) setMobileFormOpen(true);
  };

  const handleClearForm = () => {
    setEditingAddress(null);
    setFormData(initialFormData);
    setMobileFormOpen(false);
  };

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  // Auto-populate city & state when a valid 6-digit pincode is entered
  useEffect(() => {
    const pin = formData.pincode;
    if (!pin || pin.length !== 6) return;
    let cancelled = false;
    lookupPincode(pin).then((result) => {
      if (cancelled || !result) return;
      setFormData((prev) => ({
        ...prev,
        city: prev.city || result.city,
        state: prev.state || result.state,
      }));
    });
    return () => {
      cancelled = true;
    };
  }, [formData.pincode]);

  const handleSaveAddress = async () => {
    if (!formData.addressLabel || !formData.fullAddress || !formData.city || !formData.pincode) {
      setError(t('savedAddresses.fillRequired'));
      return;
    }

    setSaving(true);
    setError('');

    try {
      let config;
      if (editingAddress) {
        const addressId = editingAddress.savedAddressId || editingAddress.addressId;
        config = buildApiConfig(endPointEnums.UPDATE_SAVED_ADDRESS, {
          addressId,
          ...formData,
        });
      } else {
        config = buildApiConfig(endPointEnums.CREATE_SAVED_ADDRESS, formData);
      }

      const response = await makeApiCall(config);
      if (response?.success) {
        await fetchAddresses();
        handleClearForm();
      } else {
        setError(response?.message || t('savedAddresses.failedToSave'));
      }
    } catch (err) {
      console.error('[SavedAddresses] Save error:', err);
      setError(t('savedAddresses.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleSetDefault = async (addressId) => {
    try {
      const config = buildApiConfig(endPointEnums.SET_DEFAULT_ADDRESS, {
        addressId,
      });
      const response = await makeApiCall(config);
      if (response?.success) await fetchAddresses();
    } catch (err) {
      console.error('[SavedAddresses] Set default error:', err);
      setError(t('savedAddresses.failedToSetDefault'));
    }
  };

  const handleDeleteConfirm = (address) => {
    setAddressToDelete(address);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteAddress = async () => {
    if (!addressToDelete) return;

    setSaving(true);
    try {
      const config = buildApiConfig(endPointEnums.DELETE_SAVED_ADDRESS, {
        addressId: addressToDelete.savedAddressId || addressToDelete.addressId,
      });
      const response = await makeApiCall(config);
      if (response?.success) {
        await fetchAddresses();
        setDeleteConfirmOpen(false);
        setAddressToDelete(null);
        if (editingAddress?.addressId === addressToDelete.addressId) handleClearForm();
      } else {
        setError(response?.message || t('savedAddresses.failedToDelete'));
      }
    } catch (err) {
      console.error('[SavedAddresses] Delete error:', err);
      setError(t('savedAddresses.deleteError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading && addresses.length === 0) {
    return (
      <LayoutContainer>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
            gap: 2,
          }}
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <LocationOn sx={{ fontSize: 28, color: '#fff' }} />
            </Box>
          </motion.div>
          <Typography sx={{ color: '#64748b', fontWeight: 500 }}>
            {t('savedAddresses.loadingAddresses')}
          </Typography>
        </Box>
      </LayoutContainer>
    );
  }

  return (
    <LayoutContainer>
      <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', pb: 6 }}>
        {/* Header */}
        <Box
          sx={{
            background: BUSINESS_COLORS.gradient,
            pt: { xs: 3, md: 4 },
            pb: { xs: 6, md: 8 },
            px: { xs: 2, md: 4 },
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: -50,
              right: -50,
              width: 200,
              height: 200,
              borderRadius: '50%',
              bgcolor: alpha('#fff', 0.1),
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -80,
              left: -80,
              width: 250,
              height: 250,
              borderRadius: '50%',
              bgcolor: alpha('#fff', 0.05),
            }}
          />

          <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={2} alignItems="center">
                <IconButton
                  onClick={() => router.back()}
                  sx={{
                    bgcolor: alpha('#fff', 0.14),
                    color: '#fff',
                    '&:hover': { bgcolor: alpha('#fff', 0.22) },
                  }}
                >
                  <ArrowBack />
                </IconButton>
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      color: '#fff',
                      fontSize: { xs: '1.5rem', md: '2rem' },
                    }}
                  >
                    {t('savedAddresses.pageTitle')}
                  </Typography>
                  <Typography sx={{ color: alpha('#fff', 0.8), fontSize: '0.9rem' }}>
                    {addresses.length > 0
                      ? t('savedAddresses.addressesSaved', { count: addresses.length })
                      : t('savedAddresses.noAddressesSavedYet')}
                  </Typography>
                </Box>
              </Stack>

              {!isDesktop && (
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => {
                    handleClearForm();
                    setMobileFormOpen(true);
                  }}
                  sx={{
                    bgcolor: '#fff',
                    color: BUSINESS_COLORS.accent,
                    fontWeight: 700,
                    textTransform: 'none',
                    borderRadius: 2,
                    px: 2,
                    '&:hover': { bgcolor: '#fff' },
                  }}
                >
                  {t('savedAddresses.add')}
                </Button>
              )}
            </Stack>
          </Container>
        </Box>

        {/* Content */}
        <Container maxWidth="xl" sx={{ mt: -4, position: 'relative', zIndex: 2 }}>
          <AnimatePresence>
            {error && (
              <MotionBox
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                sx={{ mb: 3 }}
              >
                <Alert severity="error" onClose={() => setError('')} sx={{ borderRadius: 2 }}>
                  {formatApiError(error)}
                </Alert>
              </MotionBox>
            )}
          </AnimatePresence>

          <Grid container spacing={3}>
            <Grid item xs={12} lg={5}>
              <AnimatePresence mode="wait">
                {addresses.length > 0 ? (
                  <motion.div variants={containerVariants} initial="hidden" animate="visible">
                    <Stack spacing={2} sx={{ pt: 1.5 }}>
                      {addresses.map((address) => (
                        <AddressCard
                          key={address.savedAddressId || address.addressId}
                          address={address}
                          isSelected={
                            (editingAddress?.savedAddressId || editingAddress?.addressId) ===
                            (address.savedAddressId || address.addressId)
                          }
                          onEdit={handleSelectAddress}
                          onDelete={handleDeleteConfirm}
                          onSetDefault={handleSetDefault}
                        />
                      ))}
                    </Stack>
                  </motion.div>
                ) : (
                  <MotionBox
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    sx={{
                      textAlign: 'center',
                      py: 8,
                      px: 3,
                      bgcolor: '#fff',
                      borderRadius: 3,
                      border: '1px solid #e2e8f0',
                    }}
                  >
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: 4,
                        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 3,
                      }}
                    >
                      <LocationOn sx={{ fontSize: 40, color: BUSINESS_COLORS.accent }} />
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: BUSINESS_COLORS.primary,
                        mb: 1,
                      }}
                    >
                      {t('savedAddresses.noAddressesTitle')}
                    </Typography>
                    <Typography
                      sx={{
                        color: '#64748b',
                        mb: 3,
                        maxWidth: 300,
                        mx: 'auto',
                      }}
                    >
                      {t('savedAddresses.noAddressesDescription')}
                    </Typography>
                    {!isDesktop && (
                      <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => setMobileFormOpen(true)}
                        sx={{
                          background: `linear-gradient(135deg, ${BUSINESS_COLORS.accent} 0%, #d97706 100%)`,
                          fontWeight: 700,
                          textTransform: 'none',
                          px: 4,
                          py: 1.5,
                          borderRadius: 3,
                        }}
                      >
                        {t('savedAddresses.addFirstAddress')}
                      </Button>
                    )}
                  </MotionBox>
                )}
              </AnimatePresence>
            </Grid>

            {isDesktop && (
              <Grid item lg={7}>
                <Box sx={{ position: 'sticky', top: 24 }}>
                  <AddressForm
                    formData={formData}
                    onChange={handleChange}
                    onSave={handleSaveAddress}
                    onClear={handleClearForm}
                    loading={saving}
                    setFormData={setFormData}
                    isEditing={!!editingAddress}
                    isDesktop={isDesktop}
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </Container>

        {!isDesktop && (
          <MobileFormDialog open={mobileFormOpen} onClose={() => setMobileFormOpen(false)}>
            <AddressForm
              formData={formData}
              onChange={handleChange}
              onSave={handleSaveAddress}
              onClear={handleClearForm}
              loading={saving}
              setFormData={setFormData}
              isEditing={!!editingAddress}
              isDesktop={false}
            />
          </MobileFormDialog>
        )}

        <DeleteDialog
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
          onConfirm={handleDeleteAddress}
          address={addressToDelete}
          loading={saving}
        />
      </Box>
    </LayoutContainer>
  );
}
